import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../FaIcon';
import {ScreenTracker} from '../../utils/ScreenTracker';

export const UPGRADE_PLAN_PATH = '/agency/subscription#plan-change';

/**
 * El límite de usuarios cliente del Portal, con la salida para ampliarlo: uso + explicación +
 * «Mejorar plan». Es la tarjeta que antes vivía sólo en la invitación desde `/agency-team`
 * (`renderClientCardDisabled`) y ahora se muestra en TODO lugar donde se invita a un usuario
 * cliente — el tab Portal del cliente, conexiones, el equipo — y cuando el backend rechaza la
 * invitación por `CLIENT_USERS_LIMIT_REACHED`.
 *
 * Con `max === 0` el plan no incluye el Portal: se dice eso, sin contador.
 * El CTA va a la pantalla de suscripción (ancla del cambio de plan), que es el flujo que ya existe
 * para agencias que pagan y para las que están en free trial.
 *
 * Mide cuántas veces se muestra y cuántas se hace clic (`ScreenTracker.recordEvent`), separando
 * el cupo lleno (`paywall_client_users`) del plan sin portal (`paywall_client_portal_off`) para
 * que una métrica no infle a la otra. `track={false}` donde no hay alguien chocando con el límite
 * al invitar (el modal de edición de un miembro).
 *
 * - `inline`: dentro de una sección que ya muestra el uso del cupo en su encabezado («Usuarios con
 *   acceso»), sin el rótulo «Cliente» del selector de tipo ni el contador repetido.
 * - `existingUserHint`: donde el formulario de invitación sigue abierto. Sumarle un cliente a
 *   alguien que YA es usuario del portal no consume cupo (`GrantPortalAccess` en el backend), así
 *   que con el cupo lleno eso todavía se puede hacer.
 * - `hideUsage`: no mostrar el contador aunque haya tope. Lo usa el modal cuando el contador que
 *   tiene contradice un rechazo por límite que el backend acaba de confirmar (p. ej. «2/5»).
 *
 * En `inline` no hay rótulo arriba, así que el hecho principal («Alcanzaste el límite…») lleva la
 * tipografía del rótulo: si no, se leía igual que las dos explicaciones (QA-VIS-cupo-03).
 */
export function ClientUsersLimitNotice({current, max, className = '', inline = false,
                                          existingUserHint = false, track = true, hideUsage = false}) {
    const {t} = useTranslation();
    const isFeatureOff = max === 0;
    const eventName = isFeatureOff ? 'paywall_client_portal_off' : 'paywall_client_users';

    useEffect(() => {
        if (track) ScreenTracker.recordEvent(eventName);
    }, [track, eventName]);

    return (
        <div className={`invite-type-card -upgrade client-users-limit-notice ${className}`}
             data-testid="client-users-limit-notice">
            <div className="invite-type-card-icon">
                <FaIcon iconName="user-tag"/>
            </div>
            <div className="invite-type-card-content">
                {!inline && <span className="invite-type-card-label">{t('invite_user.client_option')}</span>}
                <div className="invite-upgrade-info">
                    {isFeatureOff ? (
                        <span className="invite-upgrade-message">{t('invite_user.client_disabled_feature_off')}</span>
                    ) : (
                        <>
                            {!inline && !hideUsage && max != null && (
                                <span className="invite-upgrade-badge">
                                    {t('invite_user.client_usage', {current: current || 0, max})}
                                </span>
                            )}
                            <span className={inline ? 'invite-type-card-label' : 'invite-upgrade-message'}>
                                {t('invite_user.client_limit_reached')}
                            </span>
                            <span className="invite-upgrade-message">{t('invite_user.client_limit_upgrade_explainer')}</span>
                            {existingUserHint && (
                                <span className="invite-upgrade-message">{t('invite_user.client_limit_existing_user_hint')}</span>
                            )}
                        </>
                    )}
                    <a className="btn -alt invite-upgrade-cta" href={UPGRADE_PLAN_PATH}
                       onClick={() => { if (track) ScreenTracker.recordEvent(`${eventName}_cta`); }}>
                        <FaIcon iconName="arrow-up-right-from-square"/>
                        {t('invite_user.upgrade_plan_cta')}
                    </a>
                </div>
            </div>
        </div>
    );
}
